import { Button, Card, Input, Modal, PageHeader, Select, Space, Spin, Table, Tag } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { getImplementationGuide, getStructureDefProfileUrl } from '../../utils/util';
import React, { useEffect, useState } from 'react';

const { Option } = Select;

const AddMappingModal = props => {
  const {
    addMappingModal,
    addStructureDefElements,
    addProfileToMapping,
    dataSource,
    profileUrl,
    profiles,
    resourcetype,
    setParentState,
  } = props;
  const [mappingSearchText, setmappingSearchTexts] = useState('');
  const [options, setOptions] = useState([]);
  const [dataSourceVisible, setDataSourceVisible] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileSelected, setProfileSelected] = useState(false);
  const [value, setValue] = useState('');

  const columns = [
    {
      title: 'Root Path',
      dataIndex: 'root_element',
      key: 'root_element',
      width: 300,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.root_element.localeCompare(b.root_element),
    },
    {
      title: 'Definition',
      dataIndex: 'definition',
      key: 'definition',
    },
    {
      title: 'Required',
      width: 100,
      sorter: (a, b) => a.min.localeCompare(b.min),
      render: (text, record) => {
        return <Space size="middle">{record.min === '1' && <Tag color="red">Required</Tag>}</Space>;
      },
    },
    {
      title: 'Min',
      dataIndex: 'min',
      key: 'min',
      width: 60,
      sorter: (a, b) => a.min.localeCompare(b.min),
    },
    {
      title: 'Max',
      dataIndex: 'max',
      key: 'max',
      width: 60,
      sorter: (a, b) => a.max.localeCompare(b.max),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 125,
      render: (text, record) => {
        const alreadyExists = dataSource.filter(mapping =>
          mapping.path.includes(record.root_element.replace('#', '').replace('[x]', '')),
        );
        const disabled = alreadyExists.length > 0;

        return (
          <Space size="middle">
            <Button
              disabled={disabled}
              data-cy={`${record.root_element.replace('[#]', '')}-add-btn`}
              onClick={() => addStructureDefElements(record)}
            >
              Add
            </Button>
          </Space>
        );
      },
    },
  ];

  const pathColumns = [
    {
      title: 'Path',
      dataIndex: 'path',
      key: 'path',
      width: 200,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Allowed Values',
      dataIndex: 'allowed_values',
      key: 'allowed_values',
      render: (text, record) => {
        if (record.allowed_values == null) return null;

        const optionsList = record.allowed_values.map(val => {
          return <Option value={val}>{val}</Option>;
        });

        return (
          <Space size="middle">
            <Select defaultValue={record.allowed_values[0]}>{optionsList}</Select>
          </Space>
        );
      },
    },
  ];

  /**
   * Updates the search string value to filter out results
   * of potential structuredef profile URLs
   * @param {*} val
   */
  const handleChange = val => {
    setValue(val);
  };

  /**
   * Generates dropdowns that display all nested elements
   * under the root_element of a structuredef row.
   */
  const getExpandedRows = data => {
    // see: https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/no-unstable-nested-components.md
    const sortedRecords = [...data].sort((a, b) => a.root_element.localeCompare(b.root_element));
    const expRows = sortedRecords.map(record => {
      return (
        <Card>
          <Table
            dataSource={record.paths}
            // showHeader={false}
            rowKey="id"
            style={{ background: 'none' }}
            pagination={false}
            columns={pathColumns}
          />
        </Card>
      );
    });

    return expRows;
  };

  /**
   * Gets structuredef elements for a given IG profile_url
   */
  const getStructureDefElements = async () => {
    const elements = await getStructureDefProfileUrl(value);
    return elements;
  };

  /**
   * Filters structuredef rows based on search string,
   * mappingSearchText
   */
  const getDataSourceVis = async () => {
    const structureDef = await getStructureDefElements();
    const structuredefElements = structureDef.elements ? structureDef.elements : [];
    const dataSourceVis = structuredefElements.filter(element => {
      return (
        element.root_element.includes(mappingSearchText) ||
        element.definition.includes(mappingSearchText)
      );
    });
    return dataSourceVis;
  };

  /**
   * Callback function for Mappings search / filter.
   */
  const searchMappings = async e => {
    setmappingSearchTexts(e.target.value);
    setDataSourceVisible(await getDataSourceVis());
  };

  /**
   * After user selects a profile, gets the structuredef
   * data from the db to display to the user.
   * If the mapping/file did not already have an IG profile
   * set under meta.profile[0], this will automatically add it.
   */
  const handleProfileSelect = async () => {
    setLoading(true);
    const data = await getDataSourceVis();
    setDataSourceVisible(data);
    const profile = getImplementationGuide(dataSource);

    if (!profile) {
      addProfileToMapping(value);
    }

    setExpandedRows(getExpandedRows(data));

    setProfileSelected(true);
    setLoading(false);
  };

  /**
   * Generates dropdown options for profiles to choose from
   */
  const getOptions = () => {
    const opts = profiles.map(profile => {
      if (profile.toLowerCase().includes(value.toLowerCase()) || value === '') {
        return (
          <Option data-cy={profile} key={profile}>
            {profile}
          </Option>
        );
      }

      return null;
    });

    setLoading(false);
    return opts;
  };

  useEffect(() => {
    setDataSourceVisible([]);
    setProfileSelected(false);

    if (addMappingModal) {
      (async () => {
        setValue(profileUrl || '');
        setOptions(getOptions());
        setDataSourceVisible(await getDataSourceVis());
        setExpandedRows(getExpandedRows());
      })();
    } else {
      setValue('');
      setmappingSearchTexts('');
    }
  }, [addMappingModal, profiles]);

  return (
    <Modal
      title="Add Elements"
      centered
      footer={null}
      width="75%"
      visible={addMappingModal}
      onOk={() => [setParentState({ addMappingModal: false }), setDataSourceVisible([])]}
      onCancel={() => [setParentState({ addMappingModal: false }), setDataSourceVisible([])]}
    >
      <PageHeader
        className="site-page-header"
        onBack={null}
        title={resourcetype}
        subTitle={
          <a href={value} target="_blank" rel="noreferrer">
            {value}
          </a>
        }
      />

      {loading && <Spin />}

      {profileSelected && !loading && (
        <>
          <Input
            style={{ width: '350px', marginBottom: 20 }}
            addonBefore="Search:"
            onChange={searchMappings}
          />
          <Table
            dataSource={dataSourceVisible}
            expandable={{
              expandedRowRender: (_, i) => {
                return expandedRows[i];
              },
              rowExpandable: record => record.paths && record.paths.length > 0,
              // NOTE: this eslint error is disabled since it doesn't seem to work well
              // with how the expand callback works
              // eslint-disable-next-line react/no-unstable-nested-components
              expandIcon: ({ expanded, onExpand, record }) => {
                if (record.paths == null || record.paths.length === 0) {
                  return null;
                }

                if (expanded) {
                  return <DownOutlined onClick={e => onExpand(record, e)} />;
                }

                return <RightOutlined onClick={e => onExpand(record, e)} />;
              },
            }}
            rowKey="id"
            pagination={{ pageSize: 100 }}
            scroll={{ y: 500 }}
            size="small"
            columns={columns}
          />
        </>
      )}

      {!loading && !profileSelected && (
        <Card title="FHIR Profile">
          <p>
            Please select a{' '}
            <a href="https://www.hl7.org/fhir/profiling.html" target="_blank" rel="noreferrer">
              FHIR profile
            </a>{' '}
            to associate with this mapping:
          </p>
          <Select
            showSearch
            value={value}
            placeholder="Please select a FHIR profile"
            style={{ minWidth: '100%' }}
            filterOption
            data-cy="profile-select-input"
            onSearch={handleChange}
            onChange={handleChange}
            placement="bottomRight"
          >
            {options}
          </Select>
          <br />
          <Button
            disabled={!value}
            style={{ marginTop: 10 }}
            type="primary"
            data-cy="profile-ok-btn"
            onClick={handleProfileSelect}
          >
            Ok
          </Button>
        </Card>
      )}
    </Modal>
  );
};

export default AddMappingModal;
